import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Camera, Save, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const roleLabels = {
  super_admin: 'Super Admin',
  finance_admin: 'Finance Admin',
  client_admin: 'Client Admin',
  talent_admin: 'Talent Admin',
};

const AdminProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await api.get('/admin/profile/me');
      setProfile(res.data);
      setDisplayName(res.data.display_name || '');
      setBio(res.data.bio || '');
    } catch (e) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/profile/me', { display_name: displayName, bio });
      toast.success('Profile saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const res = await api.post('/admin/profile/upload-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Photo updated (${res.data.size_kb}KB)`);
      setProfile((p) => ({ ...p, profile_photo_url: res.data.profile_photo_url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const photoSrc = profile?.profile_photo_url
    ? (profile.profile_photo_url.startsWith('/')
        ? `${process.env.REACT_APP_BACKEND_URL}${profile.profile_photo_url}`
        : profile.profile_photo_url)
    : null;
  const initials = (displayName || profile?.email || 'A').slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-profile-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Shield className="w-4 h-4 text-cyan-500" />
            {roleLabels[profile?.admin_role] || 'Admin'}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-500 mt-1">Update your photo, display name, and bio.</p>
        </div>

        {/* Photo Card */}
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-cyan-500" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-28 h-28 rounded-full bg-gray-100 overflow-hidden border-2 border-cyan-400 flex items-center justify-center">
                  {photoSrc ? (
                    <img
                      src={photoSrc}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      data-testid="admin-profile-photo"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-3xl font-bold text-gray-400" data-testid="admin-profile-initials">
                      {initials}
                    </span>
                  )}
                </div>
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-full">
                    <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="admin-photo-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  data-testid="admin-photo-upload-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {photoSrc ? 'Replace Photo' : 'Upload Photo'}
                </Button>
                <p className="text-xs text-gray-500">JPG, PNG, WEBP. Max 5MB.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Info Card */}
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle>Profile Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={profile?.email || ''} disabled className="bg-gray-50" data-testid="admin-email" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <div className="flex items-center gap-2">
                <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200">
                  {roleLabels[profile?.admin_role] || 'Admin'}
                </Badge>
                {profile?.admin_role === 'super_admin' && (
                  <span className="text-xs text-gray-500">Hidden from non-super-admin viewers</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Mel Rodriguez"
                maxLength={120}
                data-testid="admin-display-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Short bio shown to other admins..."
                rows={4}
                data-testid="admin-bio-input"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="admin-profile-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminProfile;
